import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import Landing from "@/pages/Landing";
import Pricing from "@/pages/Pricing";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Prompts from "@/pages/Prompts";
import Runs from "@/pages/Runs";
import Optimizer from "@/pages/Optimizer";
import SchemaGen from "@/pages/SchemaGen";
import LlmsTxt from "@/pages/LlmsTxt";
import Alerts from "@/pages/Alerts";
import Settings from "@/pages/Settings";
import Artefacts from "@/pages/Artefacts";
import Competitors from "@/pages/Competitors";
import Services from "@/pages/Services";
import Citations from "@/pages/Citations";
import SentimentPage from "@/pages/Sentiment";
import Recommendations from "@/pages/Recommendations";
import AtlasLanding from "@/pages/AtlasLanding";
import AtlasView from "@/pages/AtlasView";
import AtlasScanning from "@/pages/AtlasScanning";
import GeoMatrix from "@/pages/GeoMatrix";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminUserDetail from "@/pages/admin/AdminUserDetail";
import AdminComps from "@/pages/admin/AdminComps";
import AdminActivity from "@/pages/admin/AdminActivity";
import AdminCosts from "@/pages/admin/AdminCosts";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminSystem from "@/pages/admin/AdminSystem";
import AdminRoute from "@/components/AdminRoute";
import ImpersonationBanner from "@/components/ImpersonationBanner";

import { AuthProvider } from "@/context/AuthContext";
import Auth0ProviderWithRouter from "@/lib/auth0Provider";
import { ProjectProvider } from "@/context/ProjectContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Callback from "@/pages/Callback";
import { Toaster } from "@/components/ui/sonner";

function Router() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/login" element={<Login />} />
      {/* Auth0 returns to /callback after login. */}
      <Route path="/callback" element={<Callback />} />
      {/* Public AI Visibility Atlas — no auth required */}
      <Route path="/atlas" element={<AtlasLanding />} />
      <Route path="/atlas/scan/:atlas_id" element={<AtlasScanning />} />
      <Route path="/atlas/:country/:brand" element={<AtlasView />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <ProjectProvider>
              <Dashboard />
            </ProjectProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/prompts"
        element={
          <ProtectedRoute>
            <ProjectProvider>
              <Prompts />
            </ProjectProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/runs"
        element={
          <ProtectedRoute>
            <ProjectProvider>
              <Runs />
            </ProjectProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/optimizer"
        element={
          <ProtectedRoute>
            <ProjectProvider>
              <Optimizer />
            </ProjectProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/artefacts"
        element={
          <ProtectedRoute>
            <ProjectProvider>
              <Artefacts />
            </ProjectProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/competitors"
        element={
          <ProtectedRoute>
            <ProjectProvider>
              <Competitors />
            </ProjectProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/citations"
        element={
          <ProtectedRoute>
            <ProjectProvider>
              <Citations />
            </ProjectProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/sentiment"
        element={
          <ProtectedRoute>
            <ProjectProvider>
              <SentimentPage />
            </ProjectProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/recommendations"
        element={
          <ProtectedRoute>
            <ProjectProvider>
              <Recommendations />
            </ProjectProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/geo"
        element={
          <ProtectedRoute>
            <ProjectProvider>
              <GeoMatrix />
            </ProjectProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/services"
        element={
          <ProtectedRoute>
            <ProjectProvider>
              <Services />
            </ProjectProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/schema"
        element={
          <ProtectedRoute>
            <ProjectProvider>
              <SchemaGen />
            </ProjectProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/llms-txt"
        element={
          <ProtectedRoute>
            <ProjectProvider>
              <LlmsTxt />
            </ProjectProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/alerts"
        element={
          <ProtectedRoute>
            <ProjectProvider>
              <Alerts />
            </ProjectProvider>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/settings"
        element={
          <ProtectedRoute>
            <ProjectProvider>
              <Settings />
            </ProjectProvider>
          </ProtectedRoute>
        }
      />
      {/* Admin console — non-admins get 404 via AdminRoute */}
      <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
      <Route path="/admin/users/:user_id" element={<AdminRoute><AdminUserDetail /></AdminRoute>} />
      <Route path="/admin/comps" element={<AdminRoute><AdminComps /></AdminRoute>} />
      <Route path="/admin/activity" element={<AdminRoute><AdminActivity /></AdminRoute>} />
      <Route path="/admin/costs" element={<AdminRoute><AdminCosts /></AdminRoute>} />
      <Route path="/admin/audit" element={<AdminRoute><AdminAudit /></AdminRoute>} />
      <Route path="/admin/system" element={<AdminRoute><AdminSystem /></AdminRoute>} />
      <Route path="/onboarding" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Landing />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Auth0ProviderWithRouter>
          <AuthProvider>
            <ImpersonationBanner />
            <Router />
            <Toaster richColors position="top-right" />
          </AuthProvider>
        </Auth0ProviderWithRouter>
      </BrowserRouter>
    </div>
  );
}
